import { DialogTitle } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UPDATE_ARCHIVED } from '../../constants/career-history'
import { useNotificationContext, usePartnerContext } from '../../context'
import {
  invalidatePricingUsage,
  useCareerGate
} from '../../features/pricing/hooks'
import { mutation } from '../../graphql'
import { useCreateLog } from '../../hooks/use-create-log'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import Modal from '../tailwind/Modal'

const RecoverCareersAction = ({ careers, updateHandler, open, setOpen }) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()
  const { partner } = usePartnerContext()
  const queryClient = useQueryClient()

  const [isLoading, setIsLoading] = useState(false)
  // Plätze, die die Auswahl beim Bestätigen gebraucht hätte (delta). Entwürfe,
  // Tour und Beispielstellen belegen keinen, deshalb nicht careers.length.
  const [blockedCount, setBlockedCount] = useState(null)

  const { createCareerHistoryLog } = useCreateLog()
  const { checkRecover } = useCareerGate()

  // Jedes Öffnen beginnt ohne den Hinweis vom letzten Mal
  useEffect(() => {
    if (open) setBlockedCount(null)
  }, [open])

  const recoverCareers = async () => {
    setIsLoading(true)

    // Defensiv noch einmal frisch zählen, die Seite hat schon vor dem Öffnen
    // geprüft. Seitdem kann jemand eine Stelle aktiviert haben. Der Hinweis
    // bleibt hier im Modal, einen zweiten Dialog darüber stapelt HeadlessUI
    // 2.1 nicht. Außerhalb von enforced gibt der Guard sofort frei.
    const check = await checkRecover(careers)
    if (!check.allowed) {
      setBlockedCount(
        typeof check.delta === 'number' ? check.delta : careers.length
      )
      setIsLoading(false)
      return
    }

    const recoverCareer = ({ id: career_id }) => {
      return Promise.all([
        mutation({
          mutation: 'updateCareer',
          input: { id: career_id, archived: false }
        }),
        createCareerHistoryLog({
          career_id,
          context: UPDATE_ARCHIVED,
          input: { archived: false }
        })
      ])
    }

    Promise.all(careers.map((career) => recoverCareer(career)))
      .then(() => {
        // Wiederhergestellte aktive Stellen belegen wieder einen Platz
        invalidatePricingUsage({
          queryClient,
          partnerId: partner?.id,
          resource: 'careers'
        })
        updateHandler()

        success(t('recover_careers.recover_career_success'))
        setIsLoading(false)
        setOpen(false)
      })
      .catch(() => setIsLoading(false))
  }

  return (
    <Modal open={open} setOpen={() => setOpen(false)} size='lg'>
      <DialogTitle className='text-center text-2xl font-medium leading-6 text-gray-900'>
        {t('recover_careers.recover_action')}
      </DialogTitle>
      <span className='mt-2 block text-center text-base text-gray-600'>
        {t('recover_careers.recover_description')}
      </span>
      {blockedCount !== null && (
        <div role='alert' className='mt-6'>
          <InfoCallout tone='critical'>
            {t('pricing.limit.careers.text_recover', { count: blockedCount })}{' '}
            {t('pricing.limit.careers.option_archive_text')}
          </InfoCallout>
        </div>
      )}
      <div className='mt-8 flex flex-row gap-3'>
        <Button.SecondaryXL
          fullWidth
          text={t('recover_careers.recover_cancel_action')}
          onClick={() => setOpen(false)}
        />
        <Button.PrimaryXL
          fullWidth
          text={
            careers.length === 1
              ? t('recover_careers.recover_action_single', {
                  count: careers.length
                })
              : t('recover_careers.recover_action_multiple', {
                  count: careers.length
                })
          }
          onClick={recoverCareers}
          isLoading={isLoading}
        />
      </div>
    </Modal>
  )
}

RecoverCareersAction.propTypes = {
  careers: PropTypes.array.isRequired,
  updateHandler: PropTypes.func,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

RecoverCareersAction.defaultProps = {
  updateHandler: () => undefined
}

export default RecoverCareersAction
